import React, { useEffect, useRef } from "react";
import Marquee from "react-fast-marquee";

const SERVICES = [
  {
    num: "01",
    title: "iOS Apps",
    sub: "Swift-native. App Store ready.",
    detail:
      "Native iOS development from idea to launch: design, build, ship, iterate. Apps that feel like Apple made them.",
    tags: ["Swift", "SwiftUI", "App Store"],
  },
  {
    num: "02",
    title: "Web Development",
    sub: "Sites, platforms, storefronts.",
    detail:
      "Fast, modern web builds: landing pages, full products, e-commerce. Engineered to load fast and convert faster.",
    tags: ["React", "E-commerce", "SaaS"],
  },
  {
    num: "03",
    title: "Game Development",
    sub: "Mechanics, worlds, polish.",
    detail:
      "Playable ideas built end to end: gameplay, engines, and worlds that people actually want to stay inside.",
    tags: ["Unity", "Unreal", "Gameplay"],
  },
  {
    num: "04",
    title: "Video Production",
    sub: "Filming, editing, post.",
    detail:
      "We shoot it and we cut it. Brand films, product videos, social content. Cinema energy at internet speed.",
    tags: ["Filming", "Editing", "Post"],
  },
  {
    num: "05",
    title: "E-commerce Growth",
    sub: "Ads that pay for themselves.",
    detail:
      "Ad management and storefronts run as one system: creative, targeting, and the site it all lands on.",
    tags: ["Meta Ads", "Google Ads", "CRO"],
  },
];

const MARQUEE_WORDS = [
  "iOS Apps",
  "Web Development",
  "Game Development",
  "Video Production",
  "E-commerce Growth",
];

function MarqueeStrip({ outline }) {
  return (
    <Marquee gradient={false} speed={45} className="py-4">
      {MARQUEE_WORDS.map((word, i) => (
        <span key={i} className="flex items-center">
          <span
            className={`pm-display whitespace-nowrap text-4xl md:text-6xl ${
              outline ? "pm-outline" : ""
            }`}
          >
            {word}
          </span>
          <span className="pm-dot" />
        </span>
      ))}
    </Marquee>
  );
}

export default function OnePage() {
  const pageRef = useRef(null);

  useEffect(() => {
    const page = pageRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("pm-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    page.querySelectorAll(".pm-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={pageRef} className="pm-page relative min-h-screen">
      <div className="pm-noise" />

      {/* ===== Top bar ===== */}
      <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 md:px-10 py-5 mix-blend-difference">
        <span className="pm-mono">Plastik Minds &reg;</span>
        <span className="pm-mono hidden md:flex items-center gap-2">
          <span
            className="pm-blink inline-block w-2 h-2 rounded-full"
            style={{ background: "var(--pm-molten)" }}
          />
          Open for shapes
        </span>
        <a href="mailto:melt@plastikminds.com" className="pm-mono hover:opacity-60 transition-opacity">
          Contact
        </a>
      </header>

      {/* ===== Hero ===== */}
      <section className="relative min-h-screen flex flex-col justify-center px-5 md:px-10 pt-28 pb-40">
        <p className="pm-mono pm-hero-in mb-8" style={{ color: "var(--pm-molten)", animationDelay: "0.1s" }}>
          An agency of moldable minds
        </p>

        <h1 className="relative z-10">
          <span className="pm-display pm-hero-in block text-[16vw] md:text-[13vw]" style={{ animationDelay: "0.25s" }}>
            Any idea.
          </span>
          <span className="pm-display pm-hero-in block text-[16vw] md:text-[13vw]" style={{ animationDelay: "0.4s" }}>
            Any{" "}
            <span className="pm-flip-hover" title="See? Any shape.">
              <span className="pm-shapeshift pm-serif" style={{ color: "var(--pm-molten)" }}>
                shape.
              </span>
            </span>
          </span>
        </h1>

        {/* Pulsing heart, recolored molten via CSS mask to match the palette */}
        <div
          className="pm-hero-in absolute right-4 md:right-16 top-24 md:top-28 w-32 h-32 md:w-56 md:h-56 pointer-events-none"
          style={{ animationDelay: "0.55s" }}
        >
          <div
            className="w-full h-full heart-pulse"
            style={{
              background: "var(--pm-molten)",
              WebkitMaskImage: `url(${require("../images/heartHeart.png")})`,
              WebkitMaskSize: "contain",
              WebkitMaskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              maskImage: `url(${require("../images/heartHeart.png")})`,
              maskSize: "contain",
              maskRepeat: "no-repeat",
              maskPosition: "center",
            }}
          />
        </div>

        <div className="pm-hero-in mt-10 max-w-md" style={{ animationDelay: "0.7s" }}>
          <p className="text-sm md:text-base leading-relaxed opacity-80">
            Plastic melts into anything. So do we: app studio on Monday,
            film crew on Friday. One team, zero handoffs.
          </p>
        </div>

        {/* Bottom marquee */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-b" style={{ borderColor: "rgba(245,231,204,0.18)" }}>
          <MarqueeStrip outline />
        </div>
      </section>

      {/* ===== Manifesto ===== */}
      <section className="px-5 md:px-10 py-32 md:py-44">
        <p className="pm-mono pm-reveal pm-flash mb-14" style={{ color: "var(--pm-molten)" }}>
         The edge
        </p>
        <h2 className="max-w-5xl">
          <span className="pm-display pm-reveal block text-5xl md:text-7xl lg:text-8xl mb-3">
            Most studios
          </span>
          <span className="pm-display pm-reveal block text-5xl md:text-7xl lg:text-8xl mb-3">
            pick a <span className="pm-outline">lane.</span>
          </span>
          <span className="pm-display pm-reveal block text-5xl md:text-7xl lg:text-8xl">
            We picked a{" "}
            <span className="pm-serif" style={{ color: "var(--pm-molten)" }}>
              material.
            </span>
          </span>
        </h2>
        <div className="pm-reveal mt-16 md:ml-auto max-w-xl">
          <p className="text-sm md:text-base leading-loose opacity-80">
            Plastik isn&apos;t a metaphor we bought at a branding workshop.
            It&apos;s how we work: melt into the problem, take its shape, set
            hard. The same minds that build your iOS app design your identity,
            film your launch video and run the ads that sell it. No agencies of
            agencies. No handoffs. No &ldquo;that&apos;s a different
            department.&rdquo;
          </p>
        </div>
      </section>

      {/* ===== Services ===== */}
      <section className="pb-32">
        <p className="pm-mono pm-reveal px-5 md:px-10 pm-flash mb-14" style={{ color: "var(--pm-molten)" }}>
         Shapes we take
        </p>
        <div className="border-b" style={{ borderColor: "rgba(245,231,204,0.18)" }}>
          {SERVICES.map((s) => (
            <div key={s.num} className="pm-row px-5 md:px-10 py-8 md:py-10">
              <div className="flex items-baseline gap-6 md:gap-12">
                <div className="flex-1">
                  <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2">
                    <h3 className="pm-display text-4xl md:text-6xl lg:text-7xl">{s.title}</h3>
                    <p className="pm-row-sub pm-mono opacity-60">{s.sub}</p>
                  </div>
                  <div className="pm-row-detail">
                    <div>
                      <div className="pt-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-10">
                        <p className="text-sm md:text-base max-w-lg leading-relaxed">
                          {s.detail}
                        </p>
                        <div className="flex gap-3 flex-wrap">
                          {s.tags.map((t) => (
                            <span
                              key={t}
                              className="pm-mono border border-current rounded-full px-4 py-1.5"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Honesty strip ===== */}
      <section className="py-24 md:py-32" style={{ background: "var(--pm-molten)", color: "var(--pm-black)" }}>
        <div className="px-5 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-6">
          {[
            ["5", "crafts under one roof"],
            ["1", "team, start to ship"],
            ["0", "handoffs, ever"],
          ].map(([n, label]) => (
            <div key={label} className="pm-reveal flex items-center gap-5 md:block">
              <div className="pm-display text-7xl md:text-9xl leading-none">{n}</div>
              <p className="pm-mono md:mt-3">{label}</p>
            </div>
          ))}
        </div>
        <div className="pm-reveal px-5 md:px-10 mt-20 max-w-2xl">
          <p className="text-sm md:text-base leading-loose font-medium">
            Full honesty: our own website kept getting bumped for client work.
            It always will. Around here your project ships first. Ours can wait.
          </p>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative px-5 md:px-10 pt-32 md:pt-44 pb-20">
        {/* The drip logo as a stamp on the letter */}
        <img
          src={require("../images/plastikText.png")}
          alt="Plastik Minds"
          className="pm-stamp absolute right-0 md:right-4 lg:right-16 top-10 md:top-16 h-40 md:h-72 w-auto pointer-events-none"
        />
        <p className="pm-mono pm-reveal mb-14" style={{ color: "var(--pm-molten)" }}>
         Your turn
        </p>
        <a href="mailto:melt@plastikminds.com" className="pm-cta-link pm-reveal block">
          <span className="pm-display block text-[13vw] md:text-[10vw] leading-none">
            Got a shape
          </span>
          <span className="pm-display block text-[13vw] md:text-[10vw] leading-none">
            in <span className="pm-serif">mind?</span>
          </span>
        </a>
        <p className="pm-reveal pm-mono mt-12 opacity-60">
          melt@plastikminds.com. Tell us the idea, we&apos;ll take its shape.
        </p>
      </section>

      {/* ===== Footer ===== */}
      <footer className="px-5 md:px-10 py-8 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-4" style={{ borderColor: "rgba(245,231,204,0.18)" }}>
        <span className="pm-mono opacity-60">&copy; 2019-2026 Plastik Minds</span>
        <span className="pm-mono opacity-60">Made of plastik. Built to last.</span>
        <a href="mailto:melt@plastikminds.com" className="pm-mono hover:opacity-60 transition-opacity">
          melt@plastikminds.com
        </a>
      </footer>
    </div>
  );
}
